import { useState } from 'react';
import { api, qs, useAction, useApi } from '../api';
import { useCan } from '../auth';
import { ConfirmDialog } from '../ui/confirm';
import { Button, Empty, fmtDate, ListToolbar, MutationError, PageHead, PAGE_SIZE, Pager, Q, Section, SortTh, StatusTag, useFilters } from '../ui/kit';
import type { StatusMap } from '../ui/format';

/** Lifecycle of an exception, always shown as a tag (never only by fading the row). */
const EXCEPTION_STATE: StatusMap = { active: ['Active', 'allow'], expired: ['Expired', 'neutral'], revoked: ['Revoked', 'neutral'] };
const stateOf = (x: { revoked_at: string | null; expires_at: string | null }) =>
  x.revoked_at ? 'revoked' : x.expires_at && Date.parse(x.expires_at) <= Date.now() ? 'expired' : 'active';

type Draft = { name: string; reason: string; policyIds: string[]; userIds: string[]; groupIds: string[]; appIds: string[]; expiresAt: string };

export function Exceptions() {
  const can = useCan();
  const filters = useFilters();
  const { sp, set, offset, setOffset } = filters;
  // Prefill from links elsewhere ("Add exception" on an alert or a policy): ?policy=&user=&app= (ids).
  const prefill = { policy: sp.get('policy'), user: sp.get('user'), app: sp.get('app') };
  const hasPrefill = !!(prefill.policy || prefill.user || prefill.app);
  const q = useApi<{ total: number; items: any[] }>(`/exceptions${qs({ state: sp.get('state'), q: sp.get('q'), sort: sp.get('sort'), limit: PAGE_SIZE, offset })}`);
  const [creating, setCreating] = useState(hasPrefill);
  const [revoking, setRevoking] = useState<{ id: string; name: string } | null>(null);
  const revoke = useAction((id: string) => api(`/exceptions/${id}/revoke`, { method: 'POST' }), () => setRevoking(null));
  const writable = can('exceptions:write');
  const closeForm = () => {
    setCreating(false);
    if (hasPrefill) filters.clear(['state', 'q', 'sort']);
  };
  return (
    <>
      <PageHead
        title="Exceptions"
        sub="An exception exempts specific people, groups or applications from specific policies — the only way to relax a stricter decision. Every exception needs a reason, is audited, and should expire."
        actions={writable && !creating && <Button variant="primary" onClick={() => setCreating(true)}>New exception</Button>}
      />
      {writable && creating && <NewException key={`${prefill.policy}:${prefill.user}:${prefill.app}`} prefill={prefill} onDone={closeForm} />}
      <Section title="Exceptions">
        <ListToolbar filters={filters} placeholder="Search name or reason…" keep={['policy', 'user', 'app']}>
          <select aria-label="State" value={sp.get('state') ?? ''} onChange={(e) => set('state', e.target.value)}>
            <option value="">Any state</option>
            {Object.entries(EXCEPTION_STATE).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </ListToolbar>
        <MutationError error={revoke.error} />
        <Q q={q}>
          {(d) => d.items.length === 0 ? <Empty title="No exceptions" /> : (
            <>
              <div className="table-wrap">
                <table className="t">
                  <thead><tr><th>Exception</th><th>State</th><th>Policies</th><th>Scope</th><SortTh filters={filters} col="expires_at">Expires</SortTh><SortTh filters={filters} col="created_at" defaultDir="desc">Created</SortTh><th /></tr></thead>
                  <tbody>{d.items.map((x) => {
                    const state = stateOf(x);
                    return (
                      <tr key={x.id}>
                        <td>{x.name}<div className="sub">{x.reason}</div></td>
                        <td><StatusTag map={EXCEPTION_STATE} value={state} /></td>
                        <td className="sub">{(x.policy_names ?? []).join(', ')}</td>
                        <td className="sub">{[...(x.group_names ?? []).map((g: string) => `group ${g}`), ...(x.user_names ?? []), ...(x.app_names ?? []).map((a: string) => `in ${a}`)].join(', ')}</td>
                        <td className="sub">{x.revoked_at ? <>revoked {fmtDate(x.revoked_at)}</> : x.expires_at ? fmtDate(x.expires_at) : <span className="text-warn">never</span>}</td>
                        <td className="sub">{fmtDate(x.created_at)}<div>{x.created_by_email}</div></td>
                        <td>{state === 'active' && writable && <Button size="small" tone="danger" onClick={() => { revoke.reset(); setRevoking({ id: x.id, name: x.name }); }}>Revoke…</Button>}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
              <Pager total={d.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
            </>
          )}
        </Q>
      </Section>
      {revoking && (
        <ConfirmDialog title={`Revoke “${revoking.name}”?`} confirmLabel="Revoke exception" danger busy={revoke.isPending} error={revoke.error}
          onConfirm={() => revoke.mutate(revoking.id)} onClose={() => setRevoking(null)}>
          <p>The exempted people and applications are subject to these policies again once browsers fetch the new policy version.</p>
        </ConfirmDialog>
      )}
    </>
  );
}

function NewException({ prefill, onDone }: { prefill: { policy: string | null; user: string | null; app: string | null }; onDone: () => void }) {
  const policies = useApi<{ items: any[] }>('/policies');
  const users = useApi<any[]>('/users/options');
  const groups = useApi<any[]>('/groups');
  const apps = useApi<any[]>('/apps/options');
  const blank: Draft = {
    name: '', reason: '', groupIds: [], expiresAt: '',
    policyIds: prefill.policy ? [prefill.policy] : [], userIds: prefill.user ? [prefill.user] : [], appIds: prefill.app ? [prefill.app] : [],
  };
  const [f, setF] = useState<Draft>(blank);
  const create = useAction(() => api('/exceptions', { method: 'POST', body: { ...f, expiresAt: f.expiresAt ? new Date(f.expiresAt).toISOString() : null } }), onDone);
  const multi = (label: string, key: 'policyIds' | 'userIds' | 'groupIds' | 'appIds', opts: Array<[string, string]>) => (
    <label className="field">{label}
      <select multiple size={5} value={f[key]} onChange={(e) => setF({ ...f, [key]: Array.from(e.target.selectedOptions, (o) => o.value) })}>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
  return (
    <Section title="New exception" aside={prefill.policy || prefill.user || prefill.app ? 'prefilled from the alert or policy you came from' : undefined}>
      <form className="mt-12" onSubmit={(e) => { e.preventDefault(); create.mutate(undefined); }}>
        <div className="form-grid">
          <label className="field">Name<input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={120} /></label>
          <label className="field">Expires <span className="hint">leave empty only for permanent exceptions</span><input type="datetime-local" value={f.expiresAt} onChange={(e) => setF({ ...f, expiresAt: e.target.value })} /></label>
          <label className="field span-all">Reason<input required minLength={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} maxLength={1000} placeholder="e.g. Legal review team approved for M&A diligence until end of quarter" /></label>
          {multi('Exempt from policies', 'policyIds', (policies.data?.items ?? []).map((p) => [p.id, p.name]))}
          {multi('For groups', 'groupIds', (groups.data ?? []).map((g) => [g.id, g.name]))}
          {multi('For people', 'userIds', (users.data ?? []).map((u) => [u.id, u.name]))}
          {multi('Only in applications', 'appIds', (apps.data ?? []).map((a) => [a.id, a.name]))}
        </div>
        <p className="explain">Within an exception, scopes combine: people/groups <i>and</i> applications. At least one scope is required.</p>
        <MutationError error={create.error} />
        <div className="actions">
          <Button type="submit" variant="primary" disabled={!f.policyIds.length || create.isPending}>Create exception</Button>
          <Button variant="ghost" onClick={onDone}>Cancel</Button>
        </div>
      </form>
    </Section>
  );
}
